import { useTranslation } from '@multaqa/i18n'
import { buttonVariants } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import { PageShell } from '@multaqa/ui/components/ui/page-shell'
import { Link, useLocation } from '@tanstack/react-router'

/**
 * The page for an address no screen answers to.
 *
 * `app` renders inside the shell (the signed-in fallback, rail and top bar stay).
 * `public` stands alone, centred, and points to sign-in instead of the dashboard.
 * The requested path sits under the panel so the user can see what they asked for.
 */
export function NotFoundScreen({ variant = 'app' }: { variant?: 'app' | 'public' }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const isPublic = variant === 'public'

  const content = (
    <>
      <EmptyState
        title={t('error.notFound.pageTitle')}
        description={t('error.notFound.pageBody')}
        action={
          isPublic ? (
            <Link to="/login" className={buttonVariants()}>
              {t('action.signIn')}
            </Link>
          ) : (
            <Link to="/" className={buttonVariants()}>
              {t('error.notFound.goToDashboard')}
            </Link>
          )
        }
      />
      <p className="text-muted-foreground text-body text-center">
        <bdi dir="ltr">{pathname}</bdi>
      </p>
    </>
  )

  if (isPublic) {
    return (
      <div
        data-testid="not-found-screen"
        className="bg-card text-card-foreground flex min-h-dvh flex-col items-center justify-center p-6"
      >
        {content}
      </div>
    )
  }

  return <PageShell data-testid="not-found-screen">{content}</PageShell>
}
